import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { money, typeLabel } from "@/data/constants";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Users, Building2, Eye, DollarSign, TrendingUp, Ban, ShieldCheck, Loader2, Percent, Tag, Trash2, ImagePlus, BadgeCheck, Receipt, Smartphone, Download, AlertTriangle, RefreshCw, KeyRound, UserPlus, X,
} from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "analytics", label: "Analytics" },
  { key: "users", label: "Users" },
  { key: "fees", label: "Fees & Offers" },
  { key: "listings", label: "Listings" },
  { key: "payments", label: "Payments" },
  { key: "verifications", label: "Verifications" },
];
const SECTION_LABELS = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));

const Stat = ({ icon: Icon, label, value, tint = "#2C3830" }) => (
  <Card className="rounded-2xl border-border/60 p-5">
    <div className="flex items-center justify-between">
      <span className="label-caps text-[#6B7280]">{label}</span>
      <Icon className="h-4 w-4" style={{ color: tint }} />
    </div>
    <p data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="mt-3 font-display text-3xl font-semibold text-[#1A1A1A]">
      {value}
    </p>
  </Card>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { refresh: refreshBranding } = useBranding();
  const { user } = useAuth();
  const perms = useMemo(
    () => (user?.is_super_admin ? [...SECTIONS.map((s) => s.key), "team"] : (user?.admin_permissions || [])),
    [user],
  );
  const can = (s) => perms.includes(s);
  const [ready, setReady] = useState(false);
  const [team, setTeam] = useState(null);
  const [audit, setAudit] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [history, setHistory] = useState([]);
  const [newInst, setNewInst] = useState("");
  const [grantForm, setGrantForm] = useState({ email: "", preset: "", permissions: [] });
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [mpesaConfig, setMpesaConfig] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [payFilter, setPayFilter] = useState({ status: "all", from: "", to: "", q: "" });

  const filteredPayments = useMemo(() => payments.filter((p) => {
    if (payFilter.status === "stuck") { if (!p.is_stuck) return false; }
    else if (payFilter.status !== "all" && p.status !== payFilter.status) return false;
    if (payFilter.q) {
      const hay = `${p.receipt} ${p.phone} ${p.listing_title}`.toLowerCase();
      if (!hay.includes(payFilter.q.toLowerCase())) return false;
    }
    if (payFilter.from && (p.created_at || "") < payFilter.from) return false;
    if (payFilter.to && (p.created_at || "") > payFilter.to + "T23:59:59") return false;
    return true;
  }), [payments, payFilter]);

  const stuckCount = useMemo(() => payments.filter((p) => p.is_stuck).length, [payments]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const jobs = [];
      const g = (url, fn) => jobs.push(api.get(url).then((r) => fn(r.data)).catch(() => {}));
      if (can("overview")) g("/admin/overview", setOverview);
      if (can("analytics")) g("/admin/timeseries?days=14", (d) => setSeries(d.series));
      if (can("users")) g("/admin/users", setUsers);
      if (can("listings")) g("/admin/listings", setListings);
      if (can("listings")) g("/institutions", setInstitutions);
      if (can("listings")) g("/admin/history", setHistory);
      if (can("payments")) { g("/admin/payments", setPayments); g("/admin/mpesa-config", setMpesaConfig); }
      if (can("verifications")) g("/admin/verifications", setVerifications);
      if (can("team")) g("/admin/team", setTeam);
      if (can("team")) g("/admin/audit", setAudit);
      if (can("fees")) {
        g("/admin/settings", (d) => setSettings({ ...d, rent_rate_pct: d.rent_rate * 100, sale_rate_pct: d.sale_rate * 100 }));
      }
      await Promise.all(jobs);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load admin data");
    } finally {
      setReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleBlock = async (u) => {
    let reason = "";
    if (!u.is_blocked) {
      reason = window.prompt(`Reason for suspending ${u.email}? This is shown to them when they try to sign in.`, "");
      if (reason === null) return;
    }
    try {
      await api.patch(`/admin/users/${u.user_id}/block`, { blocked: !u.is_blocked, reason });
      setUsers((p) => p.map((x) => (x.user_id === u.user_id ? { ...x, is_blocked: !x.is_blocked, block_reason: !u.is_blocked ? reason : "" } : x)));
      toast.success(!u.is_blocked ? "User suspended" : "User reinstated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  const downloadHistoryCsv = async () => {
    try {
      const res = await api.get("/admin/history.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rento-rented-sold-history.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export history");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This removes their account and all their listings. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((p) => p.filter((x) => x.user_id !== u.user_id));
      toast.success("User deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete user");
    }
  };

  const toggleVerify = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/verify`, { verified: !u.is_verified });
      setUsers((p) => p.map((x) => (x.user_id === u.user_id ? { ...x, is_verified: !x.is_verified } : x)));
      toast.success(!u.is_verified ? "User verified" : "Verification removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  const removeListing = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      setListings((p) => p.filter((l) => l.id !== id));
      toast.success("Listing removed");
    } catch {
      toast.error("Could not remove listing");
    }
  };

  const reconcilePayment = async (ref) => {
    try {
      const { data } = await api.post(`/mpesa/reconcile/${ref}`);
      toast.success(`Payment status: ${data.status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reconcile failed");
    }
  };

  const exportPaymentsCsv = () => {
    const headers = ["Date", "Listing", "Type", "Phone", "Amount", "Currency", "Receipt", "Status", "Provider", "Ref"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    filteredPayments.forEach((p) => lines.push([
      p.created_at ? new Date(p.created_at).toISOString() : "",
      p.listing_title, p.kind, p.phone, p.amount, p.currency,
      p.receipt || "", p.status, p.provider, p.payment_ref,
    ].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rento-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        rent_rate: Number(settings.rent_rate_pct) / 100,
        sale_rate: Number(settings.sale_rate_pct) / 100,
        min_fee_ksh: Number(settings.min_fee_ksh),
        min_fee_usd: Number(settings.min_fee_usd),
        offer_active: Boolean(settings.offer_active),
        offer_percent: Number(settings.offer_percent),
        offer_label: settings.offer_label || "",
        free_unlock: Boolean(settings.free_unlock),
        logo_url: settings.logo_url || "",
        brand_name: settings.brand_name || "RENTO",
        brand_color: settings.brand_color || "#2C3830",
        support_email: settings.support_email || "",
      };
      await api.put("/admin/settings", payload);
      toast.success("Platform settings updated");
      load();
      refreshBranding();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const setS = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  const reviewVerification = async (uid, action) => {
    try {
      await api.post(`/admin/verifications/${uid}`, { action });
      toast.success(action === "approve" ? "User verified" : "Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  const toggleGrantPerm = (key) => setGrantForm((p) => ({
    ...p,
    preset: "",
    permissions: p.permissions.includes(key) ? p.permissions.filter((x) => x !== key) : [...p.permissions, key],
  }));

  const applyPreset = (preset) => setGrantForm((p) => ({
    ...p,
    preset,
    permissions: preset && team?.presets?.[preset] ? [...team.presets[preset]] : [],
  }));

  const grantAdmin = async () => {
    try {
      await api.post("/admin/team/grant", {
        email: grantForm.email.trim(),
        permissions: grantForm.permissions,
        preset: grantForm.preset,
      });
      toast.success("Admin access granted");
      setGrantForm({ email: "", preset: "", permissions: [] });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not grant access");
    }
  };

  const inviteAdmin = async () => {
    try {
      const { data } = await api.post("/admin/team/invite", {
        email: grantForm.email.trim(),
        permissions: grantForm.permissions,
        preset: grantForm.preset,
      });
      toast.success(`Invitation emailed to ${data.email}`);
      setGrantForm({ email: "", preset: "", permissions: [] });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send invite");
    }
  };

  const addInstitution = async () => {
    if (newInst.trim().length < 2) return;
    try {
      const { data } = await api.post("/institutions", { name: newInst.trim() });
      setInstitutions(data);
      setNewInst("");
      toast.success("Institution added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add institution");
    }
  };

  const deleteInstitution = async (iid) => {
    try {
      await api.delete(`/admin/institutions/${iid}`);
      setInstitutions((p) => p.filter((x) => x.id !== iid));
      toast.success("Institution removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove");
    }
  };

  const updateMemberPerm = async (member, key) => {
    const next = member.permissions.includes(key)
      ? member.permissions.filter((x) => x !== key)
      : [...member.permissions, key];
    if (next.length === 0) { toast.error("An admin needs at least one section"); return; }
    try {
      await api.patch(`/admin/team/${member.user_id}`, { permissions: next });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const revokeAdmin = async (member) => {
    try {
      await api.delete(`/admin/team/${member.user_id}`);
      toast.success("Admin access revoked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke");
    }
  };

  const changePassword = async () => {
    if (pwForm.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update password");
    } finally {
      setPwSaving(false);
    }
  };

  const logoInput = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setS("logo_url", data.path);
      toast.success("Logo uploaded — click Save settings to apply");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" />
      </div>
    );
  }

  const exampleRentFee = settings
    ? Math.max(50000 * (Number(settings.rent_rate_pct) / 100), Number(settings.min_fee_ksh)) *
      (settings.offer_active ? 1 - Number(settings.offer_percent) / 100 : 1)
    : 0;

  const visibleSections = SECTIONS.filter((s) => can(s.key));
  const firstTab = [...visibleSections.map((s) => s.key), ...(can("team") ? ["team"] : []), "security"][0];

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <span className="label-caps text-[#D4A373]">Platform control</span>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-[#1A1A1A]">Admin dashboard</h1>
      <p className="mt-1 text-[#6B7280]">Manage users, monitor traffic & revenue, and control platform fees and offers.</p>

      <Tabs defaultValue={firstTab} className="mt-8">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-2xl bg-white p-1">
          {visibleSections.map((s) => (
            <TabsTrigger key={s.key} data-testid={`tab-${s.key}`} value={s.key} className="rounded-full">{s.label}</TabsTrigger>
          ))}
          {can("team") && (
            <TabsTrigger data-testid="tab-team" value="team" className="rounded-full">Team</TabsTrigger>
          )}
          {can("team") && (
            <TabsTrigger data-testid="tab-activity" value="activity" className="rounded-full">Activity</TabsTrigger>
          )}
          <TabsTrigger data-testid="tab-security" value="security" className="rounded-full">Security</TabsTrigger>
        </TabsList>

        {/* Overview */}
        {can("overview") && (
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Stat icon={Users} label="Total Users" value={overview.users_total} />
            <Stat icon={Building2} label="Listings" value={overview.listings_total} />
            <Stat icon={Eye} label="Total Views" value={overview.total_views} tint="#D4A373" />
            <Stat icon={TrendingUp} label="Unlocks" value={overview.payments_count} tint="#25D366" />
            <Stat icon={DollarSign} label="Revenue KSH" value={money(overview.revenue.KSH, "KSH")} tint="#2C3830" />
            <Stat icon={DollarSign} label="Revenue USD" value={money(overview.revenue.USD, "USD")} tint="#2C3830" />
            <Stat icon={Ban} label="Blocked" value={overview.users_blocked} tint="#B91C1C" />
            <Stat icon={Building2} label="Rented+Sold" value={overview.rented + overview.sold} tint="#B45309" />
          </div>
          <Card className="mt-6 rounded-2xl border-border/60 p-5">
            <p className="label-caps text-[#6B7280]">Listings by status</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Badge className="rounded-full bg-[#E7EFE9] text-[#2C3830]">Available: {overview.active}</Badge>
              <Badge className="rounded-full bg-[#FEF3C7] text-[#B45309]">Rented: {overview.rented}</Badge>
              <Badge className="rounded-full bg-[#FEE2E2] text-[#B91C1C]">Sold: {overview.sold}</Badge>
            </div>
            <p className="mt-5 label-caps text-[#6B7280]">By property type</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(overview.by_type).map(([k, v]) => (
                <Badge key={k} variant="outline" className="rounded-full">{typeLabel(k)}: {v}</Badge>
              ))}
            </div>
          </Card>
        </TabsContent>
        )}

        {/* Analytics */}
        <TabsContent value="analytics" className="mt-6 space-y-6">
          <Card className="rounded-2xl border-border/60 p-5">
            <p className="font-display text-lg text-[#2C3830]">Traffic — views & unlocks (14 days)</p>
            <div className="mt-4 h-64" data-testid="traffic-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="views" stroke="#2C3830" strokeWidth={2} dot={false} name="Views" />
                  <Line type="monotone" dataKey="unlocks" stroke="#D4A373" strokeWidth={2} dot={false} name="Unlocks" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="rounded-2xl border-border/60 p-5">
            <p className="font-display text-lg text-[#2C3830]">Revenue — KSH per day (14 days)</p>
            <div className="mt-4 h-64" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="ksh" fill="#2C3830" radius={[4, 4, 0, 0]} name="KSH" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          <Card className="rounded-2xl border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Listings</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell className="font-medium">{u.name || "—"}</TableCell>
                    <TableCell className="text-[#6B7280]">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full capitalize">{u.role}</Badge></TableCell>
                    <TableCell>{u.listings_count}</TableCell>
                    <TableCell>
                      {u.is_blocked ? (
                        <Badge className="rounded-full bg-[#FEE2E2] text-[#B91C1C]">Suspended</Badge>
                      ) : (
                        <Badge className="rounded-full bg-[#E7EFE9] text-[#2C3830]">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {u.role === "admin" ? (
                        <span className="text-xs text-[#6B7280]">—</span>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button data-testid={`verify-btn-${u.user_id}`} onClick={() => toggleVerify(u)} variant="outline" size="sm" className={`rounded-full ${u.is_verified ? "border-[#1D4ED8] text-[#1D4ED8]" : ""}`}>
                            <BadgeCheck className="mr-1.5 h-4 w-4" /> {u.is_verified ? "Verified" : "Verify"}
                          </Button>
                          <Button
                            data-testid={`block-btn-${u.user_id}`}
                            onClick={() => toggleBlock(u)}
                            variant={u.is_blocked ? "outline" : "ghost"}
                            size="sm"
                            className={`rounded-full ${u.is_blocked ? "" : "text-[#B91C1C] hover:bg-destructive/10"}`}
                          >
                            {u.is_blocked ? <><ShieldCheck className="mr-1.5 h-4 w-4" /> Reinstate</> : <><Ban className="mr-1.5 h-4 w-4" /> Suspend</>}
                          </Button>
                          <Button
                            data-testid={`delete-user-${u.user_id}`}
                            onClick={() => deleteUser(u)}
                            variant="ghost"
                            size="sm"
                            className="rounded-full text-[#B91C1C] hover:bg-destructive/10"
                          >
                            <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Fees & Offers */}
        {can("fees") && (
        <TabsContent value="fees" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="rounded-2xl border-border/60 p-6">
              <p className="flex items-center gap-2 font-display text-lg text-[#2C3830]"><Percent className="h-4 w-4 text-[#D4A373]" /> Unlock fee rates</p>
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Rent rate (%)</Label>
                    <Input data-testid="rent-rate" type="number" step="0.01" value={settings.rent_rate_pct} onChange={(e) => setS("rent_rate_pct", e.target.value)} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Sale rate (%)</Label>
                    <Input data-testid="sale-rate" type="number" step="0.01" value={settings.sale_rate_pct} onChange={(e) => setS("sale_rate_pct", e.target.value)} className="rounded-xl" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Min fee (KSH)</Label>
                    <Input data-testid="min-ksh" type="number" value={settings.min_fee_ksh} onChange={(e) => setS("min_fee_ksh", e.target.value)} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Min fee (USD)</Label>
                    <Input data-testid="min-usd" type="number" value={settings.min_fee_usd} onChange={(e) => setS("min_fee_usd", e.target.value)} className="rounded-xl" />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="rounded-2xl border-border/60 p-6">
              <p className="flex items-center gap-2 font-display text-lg text-[#2C3830]"><Tag className="h-4 w-4 text-[#D4A373]" /> Promotional offer</p>
              <div className="mt-5 space-y-4">
                <div className="flex items-center justify-between rounded-xl border border-border p-4">
                  <div>
                    <p className="font-medium text-[#2C3830]">Offer active</p>
                    <p className="text-sm text-[#6B7280]">Apply a platform-wide discount on unlock fees</p>
                  </div>
                  <Switch data-testid="offer-active" checked={settings.offer_active} onCheckedChange={(v) => setS("offer_active", v)} />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Discount (%)</Label>
                  <Input data-testid="offer-percent" type="number" value={settings.offer_percent} onChange={(e) => setS("offer_percent", e.target.value)} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Offer label (shown to buyers)</Label>
                  <Input data-testid="offer-label" value={settings.offer_label} onChange={(e) => setS("offer_label", e.target.value)} placeholder="e.g. Jamhuri Day Sale — 20% off" className="rounded-xl" />
                </div>
                <div className="rounded-xl bg-[#F7F5F0] p-4 text-sm text-[#2C3830]">
                  Example: rent listing of {money(50000, "KSH")} → unlock fee{" "}
                  <span className="font-semibold" data-testid="fee-example">{money(Math.round(exampleRentFee * 100) / 100, "KSH")}</span>
                  {settings.offer_active && Number(settings.offer_percent) > 0 && <span className="ml-1 text-[#25D366]">(offer applied)</span>}
                </div>
              </div>
            </Card>
          </div>
          <Card data-testid="free-unlock-card" className="mt-6 rounded-2xl border-border/60 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="flex items-center gap-2 font-display text-lg text-[#2C3830]"><ShieldCheck className="h-4 w-4 text-[#25D366]" /> Free unlock mode</p>
                <p className="mt-1 text-sm text-[#6B7280]">Let buyers reveal contact details for free (skips payment). Use this before M-Pesa goes live, then turn it off once your Daraja keys are set.</p>
              </div>
              <Switch data-testid="free-unlock-toggle" checked={!!settings.free_unlock} onCheckedChange={(v) => setS("free_unlock", v)} />
            </div>
            {settings.free_unlock && (
              <div className="mt-4 rounded-xl bg-[#FEF3C7] p-3 text-sm text-[#92400E]">
                Free unlock is ON — no payment is required to reveal contacts.
              </div>
            )}
          </Card>
          <Card className="mt-6 rounded-2xl border-border/60 p-6">
            <p className="flex items-center gap-2 font-display text-lg text-[#2C3830]"><ImagePlus className="h-4 w-4 text-[#D4A373]" /> Branding</p>
            <p className="mt-1 text-sm text-[#6B7280]">Set your brand name, accent colour and icon shown across the app and browser tab.</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Brand name</Label>
                <Input data-testid="brand-name-input" value={settings.brand_name || ""} onChange={(e) => setS("brand_name", e.target.value)} placeholder="RENTO" className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Brand colour</Label>
                <div className="flex items-center gap-2">
                  <input data-testid="brand-color-input" type="color" value={settings.brand_color || "#2C3830"} onChange={(e) => setS("brand_color", e.target.value)} className="h-10 w-14 cursor-pointer rounded-lg border border-border bg-white p-1" />
                  <Input value={settings.brand_color || "#2C3830"} onChange={(e) => setS("brand_color", e.target.value)} className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="label-caps text-[#6B7280]">Support / appeal email</Label>
                <Input data-testid="support-email-input" type="email" value={settings.support_email || ""} onChange={(e) => setS("support_email", e.target.value)} placeholder="support@yourdomain.com" className="rounded-xl" />
                <p className="text-xs text-[#6B7280]">Shown to suspended users so they can appeal. Leave blank to use the platform reply-to address.</p>
              </div>
            </div>
            <p className="mt-5 label-caps text-[#6B7280]">Platform icon</p>
            <div className="mt-3 flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border bg-[#2C3830]">
                {settings.logo_url ? (
                  <img data-testid="logo-preview" src={fileUrl(settings.logo_url)} alt="Platform logo" className="h-full w-full object-cover" />
                ) : (
                  <Building2 className="h-7 w-7 text-white" />
                )}
              </div>
              <input ref={logoInput} type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} />
              <Button data-testid="upload-logo-btn" type="button" variant="outline" onClick={() => logoInput.current?.click()} disabled={uploadingLogo} className="rounded-full">
                {uploadingLogo ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</> : <><ImagePlus className="mr-2 h-4 w-4" /> Choose image</>}
              </Button>
              {settings.logo_url && (
                <Button data-testid="remove-logo-btn" type="button" variant="ghost" onClick={() => setS("logo_url", "")} className="rounded-full text-[#B91C1C] hover:bg-destructive/10">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Remove
                </Button>
              )}
            </div>
          </Card>
          <div className="mt-6 flex justify-end">
            <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={savingSettings} className="rounded-full bg-[#2C3830] px-8 text-white hover:bg-[#3A4A40]">
              {savingSettings ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Save settings"}
            </Button>
          </div>
        </TabsContent>
        )}

        {/* Listings */}
        <TabsContent value="listings" className="mt-6">
          <Card className="rounded-2xl border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-listing-${l.id}`}>
                    <TableCell className="font-medium max-w-[220px] truncate">{l.title}</TableCell>
                    <TableCell className="text-[#6B7280]">{l.town}, {l.county}</TableCell>
                    <TableCell>{money(l.price, l.currency)}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full capitalize">{l.status}</Badge></TableCell>
                    <TableCell>{l.views}</TableCell>
                    <TableCell className="text-right">
                      <Button data-testid={`admin-del-${l.id}`} onClick={() => removeListing(l.id)} variant="ghost" size="icon" className="rounded-full text-[#B91C1C] hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card data-testid="admin-institutions" className="mt-6 rounded-2xl border-border/60 p-5">
            <h3 className="font-display text-lg font-medium text-[#1A1A1A]">Student institutions</h3>
            <p className="mt-1 text-sm text-[#6B7280]">Manage the institutions listers can pick from. Listers can also add their own while posting.</p>
            <div className="mt-4 flex gap-2">
              <Input
                data-testid="admin-new-institution"
                value={newInst}
                onChange={(e) => setNewInst(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addInstitution(); }}
                placeholder="Add an institution"
                className="rounded-xl"
              />
              <Button data-testid="admin-add-institution-btn" onClick={addInstitution} disabled={newInst.trim().length < 2} className="rounded-full bg-[#2C3830] text-white hover:bg-[#1f281f]">Add</Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {institutions.map((i) => (
                <span key={i.id} data-testid={`inst-${i.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-white py-1 pl-3 pr-1.5 text-sm text-[#2C3830]">
                  {i.name}
                  <button
                    data-testid={`inst-del-${i.id}`}
                    onClick={() => deleteInstitution(i.id)}
                    aria-label={`Remove ${i.name}`}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:bg-destructive/10 hover:text-[#B91C1C]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
              {institutions.length === 0 && <p className="text-sm text-[#9CA3AF]">No institutions yet.</p>}
            </div>
          </Card>

          <Card data-testid="admin-history" className="mt-6 rounded-2xl border-border/60 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 p-5">
              <div>
                <h3 className="font-display text-lg font-medium text-[#1A1A1A]">Rented &amp; sold history</h3>
                <p className="mt-1 text-sm text-[#6B7280]">A permanent record of every unit marked rented or sold — kept even after the listing is auto-removed.</p>
              </div>
              <Button data-testid="history-csv-btn" onClick={downloadHistoryCsv} variant="outline" size="sm" className="rounded-full" disabled={history.length === 0}>
                <Download className="mr-1.5 h-4 w-4" /> Export CSV
              </Button>
            </div>
            {history.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#6B7280]">No closed units yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Closed on</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.listing_id} data-testid={`history-${h.listing_id}`}>
                      <TableCell className="font-medium max-w-[200px] truncate">{h.title}</TableCell>
                      <TableCell className="text-[#6B7280]">{h.town}, {h.county}</TableCell>
                      <TableCell>{money(h.price, h.currency)}</TableCell>
                      <TableCell><Badge className="rounded-full capitalize" style={{ backgroundColor: h.status === "sold" ? "#7C3AED" : "#2563EB", color: "#fff" }}>{h.status}</Badge></TableCell>
                      <TableCell className="text-xs text-[#6B7280]">{h.owner_email || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-[#6B7280]">{h.closed_at ? new Date(h.closed_at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Payments */}
        {can("payments") && (
        <TabsContent value="payments" className="mt-6 space-y-6">
          {mpesaConfig && (
            <Card data-testid="mpesa-config-card" className="rounded-2xl border-border/60 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-[#25D366]" />
                  <span className="label-caps text-[#6B7280]">M-Pesa Configuration</span>
                </div>
                {mpesaConfig.configured ? (
                  <Badge data-testid="mpesa-status" className="rounded-full bg-[#E7EFE9] text-[#2C3830]">Configured</Badge>
                ) : (
                  <Badge data-testid="mpesa-status" className="rounded-full bg-[#FEE2E2] text-[#B91C1C]">Not configured</Badge>
                )}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <p className="label-caps text-[#6B7280]">Environment</p>
                  <p data-testid="mpesa-env" className="mt-1 font-medium capitalize text-[#1A1A1A]">
                    {mpesaConfig.env}
                    {mpesaConfig.env === "sandbox" && <span className="ml-1 text-xs text-[#B45309]">(test)</span>}
                  </p>
                </div>
                <div>
                  <p className="label-caps text-[#6B7280]">Mode</p>
                  <p data-testid="mpesa-mode" className="mt-1 font-medium capitalize text-[#1A1A1A]">
                    {mpesaConfig.mode === "till" ? "Till (Buy Goods)" : "Paybill"}
                  </p>
                </div>
                <div>
                  <p className="label-caps text-[#6B7280]">Shortcode</p>
                  <p className="mt-1 font-mono font-medium text-[#1A1A1A]">{mpesaConfig.shortcode || "—"}</p>
                </div>
                <div>
                  <p className="label-caps text-[#6B7280]">{mpesaConfig.mode === "till" ? "Till Number" : "Pays To"}</p>
                  <p className="mt-1 font-mono font-medium text-[#1A1A1A]">{mpesaConfig.party_b || "—"}</p>
                </div>
              </div>
              <div className="mt-4 border-t border-border/60 pt-3">
                <p className="label-caps text-[#6B7280]">Callback URL</p>
                <p className="mt-1 break-all font-mono text-xs text-[#6B7280]">{mpesaConfig.callback_url || "—"}</p>
              </div>
            </Card>
          )}
          <Card className="rounded-2xl border-border/60 overflow-hidden">
            {stuckCount > 0 && (
              <div data-testid="stuck-alert" className="flex items-center gap-3 border-b border-[#FDE68A] bg-[#FEF3C7] px-5 py-3">
                <AlertTriangle className="h-4 w-4 shrink-0 text-[#B45309]" />
                <p className="text-sm text-[#92400E]">
                  <span className="font-semibold">{stuckCount}</span> payment{stuckCount > 1 ? "s" : ""} stuck in pending (no callback after 5 min).
                </p>
                <Button
                  data-testid="view-stuck-btn"
                  onClick={() => setPayFilter((p) => ({ ...p, status: "stuck" }))}
                  variant="outline" size="sm" className="ml-auto rounded-full border-[#B45309] text-[#B45309]"
                >
                  Review
                </Button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
              <div className="flex flex-wrap gap-1.5">
                {["all", "paid", "pending", "failed", "stuck"].map((s) => (
                  <button
                    key={s}
                    data-testid={`pay-filter-${s}`}
                    onClick={() => setPayFilter((p) => ({ ...p, status: s }))}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                      payFilter.status === s ? "bg-[#2C3830] text-white" : "bg-[#F7F5F0] text-[#6B7280] hover:bg-[#EDE9E1]"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Input
                data-testid="pay-search"
                value={payFilter.q}
                onChange={(e) => setPayFilter((p) => ({ ...p, q: e.target.value }))}
                placeholder="Search receipt, phone, listing…"
                className="h-9 w-full rounded-xl sm:w-56"
              />
              <div className="flex items-center gap-2">
                <Input data-testid="pay-date-from" type="date" value={payFilter.from} onChange={(e) => setPayFilter((p) => ({ ...p, from: e.target.value }))} className="h-9 rounded-xl" />
                <span className="text-xs text-[#6B7280]">to</span>
                <Input data-testid="pay-date-to" type="date" value={payFilter.to} onChange={(e) => setPayFilter((p) => ({ ...p, to: e.target.value }))} className="h-9 rounded-xl" />
              </div>
              <Button
                data-testid="pay-export-csv"
                onClick={exportPaymentsCsv}
                disabled={filteredPayments.length === 0}
                variant="outline" size="sm" className="ml-auto rounded-full"
              >
                <Download className="mr-1.5 h-4 w-4" /> Export CSV
              </Button>
            </div>

            {filteredPayments.length === 0 ? (
              <div data-testid="payments-empty" className="flex flex-col items-center gap-2 py-16 text-center">
                <Receipt className="h-8 w-8 text-[#D4A373]" />
                <p className="text-sm text-[#6B7280]">{payments.length === 0 ? "No transactions yet." : "No payments match these filters."}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Listing</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>M-Pesa Receipt</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((p) => (
                    <TableRow key={p.payment_ref} data-testid={`payment-row-${p.payment_ref}`} className={p.is_stuck ? "bg-[#FFFBEB]" : ""}>
                      <TableCell className="font-medium max-w-[200px] truncate">{p.listing_title}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full capitalize">{p.kind}</Badge></TableCell>
                      <TableCell className="text-[#6B7280]">{p.phone || "—"}</TableCell>
                      <TableCell>{money(p.amount, p.currency)}</TableCell>
                      <TableCell className="font-mono text-xs" data-testid={`payment-receipt-${p.payment_ref}`}>
                        {p.receipt || (p.provider === "mpesa_mock" ? "demo" : "—")}
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full ${
                          p.status === "paid" ? "bg-[#E7EFE9] text-[#2C3830]"
                          : p.status === "failed" ? "bg-[#FEE2E2] text-[#B91C1C]"
                          : "bg-[#FEF3C7] text-[#B45309]"}`}>
                          {p.is_stuck ? "stuck" : p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#6B7280] text-xs">
                        {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {p.status === "pending" ? (
                          <Button data-testid={`reconcile-btn-${p.payment_ref}`} onClick={() => reconcilePayment(p.payment_ref)} variant="outline" size="sm" className="rounded-full">
                            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reconcile
                          </Button>
                        ) : (
                          <span className="text-xs text-[#6B7280]">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
        )}

        {/* Verifications */}
        <TabsContent value="verifications" className="mt-6">
          <Card className="rounded-2xl border-border/60 overflow-hidden">
            {verifications.length === 0 ? (
              <div data-testid="verifications-empty" className="py-16 text-center text-sm text-[#6B7280]">No verification requests yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Documents</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {verifications.map((v) => (
                    <TableRow key={v.user_id} data-testid={`verification-row-${v.user_id}`}>
                      <TableCell>
                        <div className="font-medium text-[#2C3830]">{v.name}</div>
                        <div className="text-xs text-[#6B7280]">{v.email}</div>
                      </TableCell>
                      <TableCell className="capitalize">{(v.verification_role || "").replace("_", " ")}</TableCell>
                      <TableCell>
                        {(v.docs || []).length === 0 ? <span className="text-xs text-[#6B7280]">—</span> : (v.docs || []).map((d, i) => (
                          <a key={i} href={fileUrl(d)} target="_blank" rel="noreferrer" className="mr-2 text-xs text-[#1D4ED8] underline">Doc {i + 1}</a>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs text-[#6B7280]">{v.phone}{v.verification_email ? ` · ${v.verification_email}` : ""}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-full ${v.verification_status === "verified" ? "bg-[#DBEAFE] text-[#1D4ED8]" : v.verification_status === "rejected" ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#FEF3C7] text-[#B45309]"}`}>{v.verification_status}</Badge>
                      </TableCell>
                      <TableCell className="space-x-2 text-right">
                        <Button data-testid={`approve-${v.user_id}`} onClick={() => reviewVerification(v.user_id, "approve")} size="sm" className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1e40af]">Approve</Button>
                        <Button data-testid={`reject-${v.user_id}`} onClick={() => reviewVerification(v.user_id, "reject")} size="sm" variant="outline" className="rounded-full">Reject</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Team / Admins (super admin only) */}
        {can("team") && (
          <TabsContent value="team" className="mt-6 space-y-6">
            <Card className="rounded-2xl border-border/60 p-5">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-[#2C3830]" />
                <h3 className="font-display text-lg font-medium text-[#1A1A1A]">Grant admin access</h3>
              </div>
              <p className="mt-1 text-sm text-[#6B7280]">The person must already have a RENTO account. Pick a preset or choose sections manually.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="label-caps text-[#6B7280]">User email</Label>
                  <Input
                    data-testid="grant-email-input"
                    value={grantForm.email}
                    onChange={(e) => setGrantForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="person@example.com"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="label-caps text-[#6B7280]">Preset</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {Object.keys(team?.presets || {}).map((pk) => (
                      <button
                        key={pk}
                        type="button"
                        data-testid={`preset-${pk}`}
                        onClick={() => applyPreset(grantForm.preset === pk ? "" : pk)}
                        className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${grantForm.preset === pk ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border/60 bg-white text-[#2C3830]"}`}
                      >
                        {pk}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Label className="label-caps text-[#6B7280]">Sections this admin can access</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SECTIONS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`grant-perm-${s.key}`}
                      onClick={() => toggleGrantPerm(s.key)}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${grantForm.permissions.includes(s.key) ? "border-[#1D4ED8] bg-[#DBEAFE] text-[#1D4ED8]" : "border-border/60 bg-white text-[#6B7280]"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  data-testid="grant-admin-btn"
                  onClick={grantAdmin}
                  disabled={!grantForm.email.trim() || grantForm.permissions.length === 0}
                  className="rounded-full bg-[#2C3830] text-white hover:bg-[#1f281f]"
                >
                  Grant access now
                </Button>
                <Button
                  data-testid="invite-admin-btn"
                  onClick={inviteAdmin}
                  disabled={!grantForm.email.trim() || grantForm.permissions.length === 0}
                  variant="outline"
                  className="rounded-full"
                >
                  <UserPlus className="mr-2 h-4 w-4" /> Send email invite
                </Button>
              </div>
              <p className="mt-2 text-xs text-[#6B7280]">
                "Grant access now" works only if the person already has an account. "Send email invite" emails them a secure signup link.
              </p>
            </Card>

            <Card className="rounded-2xl border-border/60 overflow-hidden">
              <div className="border-b border-border/60 p-5">
                <h3 className="font-display text-lg font-medium text-[#1A1A1A]">Current admins</h3>
              </div>
              <div className="divide-y divide-border/60">
                {(team?.admins || []).map((m) => (
                  <div key={m.user_id} data-testid={`admin-row-${m.user_id}`} className="p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 font-medium text-[#2C3830]">
                          {m.name || m.email}
                          {m.is_super_admin && (
                            <Badge className="rounded-full bg-[#2C3830] text-white">Super admin</Badge>
                          )}
                        </div>
                        <div className="text-xs text-[#6B7280]">{m.email}</div>
                      </div>
                      {!m.is_super_admin && (
                        <Button
                          data-testid={`revoke-${m.user_id}`}
                          onClick={() => revokeAdmin(m)}
                          size="sm"
                          variant="outline"
                          className="rounded-full text-[#B91C1C]"
                        >
                          <X className="mr-1 h-3.5 w-3.5" /> Revoke
                        </Button>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {m.is_super_admin ? (
                        <span className="text-xs text-[#6B7280]">Full access to all sections</span>
                      ) : (
                        SECTIONS.map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            data-testid={`member-perm-${m.user_id}-${s.key}`}
                            onClick={() => updateMemberPerm(m, s.key)}
                            className={`rounded-full border px-3 py-1 text-xs transition-colors ${m.permissions.includes(s.key) ? "border-[#1D4ED8] bg-[#DBEAFE] text-[#1D4ED8]" : "border-border/60 bg-white text-[#9CA3AF]"}`}
                          >
                            {s.label}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        )}

        {/* Activity log (super admin only) */}
        {can("team") && (
          <TabsContent value="activity" className="mt-6">
            <Card className="rounded-2xl border-border/60 overflow-hidden">
              {audit.length === 0 ? (
                <div data-testid="activity-empty" className="py-16 text-center text-sm text-[#6B7280]">No admin activity recorded yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Admin</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.map((a) => (
                      <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-[#6B7280]">{a.ts ? new Date(a.ts).toLocaleString() : "—"}</TableCell>
                        <TableCell className="text-sm text-[#2C3830]">{a.actor_email}</TableCell>
                        <TableCell>
                          <Badge className="rounded-full bg-[#E7EFE9] capitalize text-[#2C3830]">{(a.action || "").replace(/_/g, " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{a.target || "—"}</TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{a.detail || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        )}

        {/* Security — change own password */}        <TabsContent value="security" className="mt-6">
          <Card className="mx-auto max-w-md rounded-2xl border-border/60 p-6">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[#2C3830]" />
              <h3 className="font-display text-lg font-medium text-[#1A1A1A]">Change your password</h3>
            </div>
            <p className="mt-1 text-sm text-[#6B7280]">Update the password for your admin account.</p>
            <div className="mt-5 space-y-4">
              <div>
                <Label className="label-caps text-[#6B7280]">Current password</Label>
                <Input
                  data-testid="pw-current"
                  type="password"
                  value={pwForm.current}
                  onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))}
                  placeholder="Leave blank if you signed in with Google"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="label-caps text-[#6B7280]">New password</Label>
                <Input
                  data-testid="pw-new"
                  type="password"
                  value={pwForm.next}
                  onChange={(e) => setPwForm((p) => ({ ...p, next: e.target.value }))}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label className="label-caps text-[#6B7280]">Confirm new password</Label>
                <Input
                  data-testid="pw-confirm"
                  type="password"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                  className="mt-1 rounded-xl"
                />
              </div>
              <Button
                data-testid="change-password-btn"
                onClick={changePassword}
                disabled={pwSaving}
                className="w-full rounded-full bg-[#2C3830] text-white hover:bg-[#1f281f]"
              >
                {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
